export function formatMoney(value: number | null | undefined): string {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return '0 ₽';
  const hasKopecks = Math.abs(num - Math.round(num)) >= 0.005;
  return `${num.toLocaleString('ru-RU', {
    minimumFractionDigits: hasKopecks ? 2 : 0,
    maximumFractionDigits: hasKopecks ? 2 : 0,
  })} ₽`;
}

export function formatMillions(value: number | null | undefined): string {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return '0,00 млн ₽';
  if (Math.abs(num) >= 1_000_000) {
    return `${(num / 1_000_000).toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} млн ₽`;
  }
  return formatMoney(num);
}

export function formatDateRu(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const d = isoDate.slice(0, 10);
  const parts = d.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

export function formatDateTimeRu(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return iso;
    return dt.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  settlement: 'Расчётный счёт',
  special_obs: 'Спецсчёт (ОБС / сопровождение)',
  treasury: 'Казначейский счёт',
  deposit: 'Депозит / Овернайт',
  tax_reserve: 'Налоговый резерв (ЕНС)',
  credit_line: 'Ссудный счёт',
};

export const BANK_PROVIDER_NAMES = {
  tochka_api: 'Банк Точка',
  alfa_api: 'Альфа-Банк',
  sber_business: 'Сбербанк',
} as const;

/** Откуда взята строка истории остатков. */
export const BALANCE_SOURCE_LABELS: Record<string, string> = {
  '1c_exchange': 'Выписка',
  manual: 'Вручную',
  snapshot: 'Снимок дня',
  payment: 'Платёж',
  tochka_api: 'Банк Точка',
  alfa_api: 'Альфа-Банк',
  sber_business: 'Сбербанк',
};

/** Откуда счёт получает данные: из банка напрямую или от человека. */
export function bankAccountSource(account: {
  connection_id: string | null;
  api_status: string;
}): { tone: 'success' | 'danger' | 'neutral'; label: string } {
  if (!account.connection_id) return { tone: 'neutral', label: 'Вручную' };
  return account.api_status === 'error'
    ? { tone: 'danger', label: 'Ошибка обмена' }
    : { tone: 'success', label: 'Из банка' };
}

export const INSTRUMENT_TYPE_LABELS: Record<string, string> = {
  credit_line_vkl: 'ВКЛ (возобновляемая линия)',
  credit_line_nkl: 'НКЛ (невозобновляемая линия)',
  term_loan: 'Срочный кредит',
  overdraft: 'Овердрафт к р/с',
  project_finance: 'Проектное финансирование',
  leasing: 'Финансовый лизинг (спецтехника)',
  bank_guarantee: 'Банковская гарантия (БГ)',
};

export const PAYMENT_CATEGORY_LABELS: Record<string, string> = {
  ks3_receipt: 'Оплата Заказчика по КС-3',
  advance_receipt: 'Аванс от Заказчика',
  retention_receipt: 'Возврат гарантийного удержания',
  other_inflow: 'Прочие поступления / резерв',
  internal_transfer: 'Внутригрупповой перевод между своими счетами',
  materials: 'МТО и кабельно-проводниковая продукция',
  subcontract: 'Субподрядные работы (СМР)',
  payroll: 'ФОТ, вахтовые и суточные',
  taxes: 'Налоги, НДС и ЕНС',
  debt_service: 'Погашение кредитов и процентов',
  leasing: 'Лизинговые платежи (техника)',
  equipment: 'Силовое оборудование и РЗА',
  logistics: 'Транспортно-экспедиционные услуги (Логистика)',
  overhead: 'Накладные и офисные расходы',
  other: 'Прочие платежи',
};

export const INFLOW_STATUS_LABELS: Record<string, string> = {
  bdds_forecast: 'План БДДС (ожидается КС-2)',
  ks2_on_review: 'КС-2 на проверке СКК Заказчика',
  ks3_signed_awaiting: 'КС-3 подписана — ждём оплату',
  partially_paid: 'Частично оплачено по КС-3',
  paid_in_full: 'Поступило в полном объёме',
  overdue_receivable: 'Просрочка оплаты по КС-3',
};
